import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@/shared/components';
import type { OtaBookingRefundLedger, OtaEarlyDepartureReason } from '@/shared/api';
import { OtaEarlyDepartureActualFields } from './OtaEarlyDepartureActualFields';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  checkOutDate: string;
  departureDate: string;
  mode: 'actual' | 'plan';
  editingPlan: boolean;
  dateError: string | null;
  reason: OtaEarlyDepartureReason;
  refundAmount: string;
  refundReason: string;
  ledger?: OtaBookingRefundLedger;
  pending: boolean;
  canSubmit: boolean;
  onDateChange: (value: string) => void;
  onReasonChange: (value: OtaEarlyDepartureReason) => void;
  onRefundAmountChange: (value: string) => void;
  onRefundReasonChange: (value: string) => void;
  onSubmit: () => void;
};

export function OtaEarlyDepartureDialog({
  open,
  onOpenChange,
  checkOutDate,
  departureDate,
  mode,
  editingPlan,
  dateError,
  reason,
  refundAmount,
  refundReason,
  ledger,
  pending,
  canSubmit,
  onDateChange,
  onReasonChange,
  onRefundAmountChange,
  onRefundReasonChange,
  onSubmit,
}: Props) {
  const planned = mode === 'plan';
  const title = planned
    ? editingPlan
      ? 'Geplante Abreise ändern'
      : 'Vorzeitige Abreise planen'
    : 'Vorzeitige Abreise erfassen';
  const submitLabel = planned
    ? editingPlan
      ? 'Plan speichern'
      : 'Abreise planen'
    : 'Abreise erfassen';
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showClose={!pending}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {planned
              ? 'Der Plan bereitet Reinigung und Vollzug vor. OTA-Vertrag, Preis, Zugang und Belegung bleiben bis dahin unverändert.'
              : 'Der OTA-Vertrag und der Preis bleiben unverändert. Zugang und offene Reinigung enden am tatsächlichen Abreisetag; verfügbare Nächte werden per ARI neu abgeleitet.'}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <label className="text-ink flex flex-col gap-1.5 text-sm font-medium">
            {planned ? 'Geplante Abreise' : 'Tatsächliche Abreise'}
            <Input
              type="date"
              value={departureDate}
              disabled={pending}
              aria-invalid={!!dateError}
              aria-describedby={dateError ? 'early-departure-date-error' : undefined}
              onChange={(event) => onDateChange(event.target.value)}
            />
          </label>
          {dateError && (
            <p id="early-departure-date-error" className="text-sealwax text-xs" role="alert">
              {dateError}
            </p>
          )}
          {planned ? (
            <p className="bg-navy/5 text-ink-soft rounded-md px-3 py-2 text-xs">
              Die vertraglichen Nächte bleiben bis zum tatsächlichen Vollzug belegt.
            </p>
          ) : (
            <OtaEarlyDepartureActualFields
              {...{
                checkOutDate,
                departureDate,
                reason,
                refundAmount,
                refundReason,
                ledger,
                pending,
                onReasonChange,
                onRefundAmountChange,
                onRefundReasonChange,
              }}
            />
          )}
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            disabled={pending}
            onClick={() => onOpenChange(false)}
          >
            Abbrechen
          </Button>
          <Button
            type="button"
            variant="brand"
            loading={pending}
            disabled={!canSubmit}
            onClick={onSubmit}
          >
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
